import { Vector3, Matrix4 } from "three";
/* import { QuadtreeUtils } from "../../infra3d"; */
import { QuadtreeUtils } from "../../infra3d/utils/quadtreeutils";
import { GEOMETRYTYPE } from "../featuretree/internal";
import { MeasureInfo } from "../measure/internal";
import { Frame } from "../api/utils/framer_v2/frame";
import { StereoGeoFrameCollection } from "./stereogeoframecollection";
import proj4 from "proj4";
import { Spatial } from "../geo/Spatial";
export class GeoFrame {
    constructor(scene, frame) {
        this.scene = scene;
        // FIXME: Try to remove this. Coupling.
        this.parentGeoFrameCollection = null;
        // REVIEW: Maybe create getters to props or fully copy of frame to be pure.
        //         Is this interface only read. Pure would be better.
        this.id = frame.id;
        this.key = frame.key;
        this.collection = frame.collection;
        this.stream = frame.stream;
        this.segment = frame.segment;
        this.type = frame.streamtype;
        this._framenumber = frame.framenumber;
        this.projection = frame.projection;
        this.images = frame.images;
        this.height = frame.height;
        this.precEasting = frame.precLongitude;
        this.precNorthing = frame.precLatitude;
        this.precHeight = frame.precHeight;
        this.timestamp = frame.timestamp;
        this.capturesystem = frame.capturesystem;
        this.framedatameta = frame.framedatameta;
        this.framedatametaID = frame.framedatametaID;
        this.campaign = frame.campaign;
        this.campaignID = frame.campaignID;
        this.playmode = frame.playmode;
        this.srid = this.scene.viewer.epsg || frame.epsg;
        this.sridHeight = frame.epsgHeight;
        const coords = proj4("EPSG:4326", `EPSG:${this.srid}`, {
            x: frame.longitude,
            y: frame.latitude,
            z: frame.height,
        });
        this.easting = coords.x;
        this.northing = coords.y;
        const opk = Spatial.rotFromWGS84({ easting: this.easting, northing: this.northing, height: this.height }, { omega: frame.omega, phi: frame.phi, kappa: frame.kappa }, this.srid);
        this.omega = opk.omega;
        this.phi = opk.phi;
        this.kappa = opk.kappa;
        const std_opk = Spatial.rotFromWGS84({ easting: this.easting, northing: this.northing, height: this.height }, { omega: frame.precOmega, phi: frame.precPhi, kappa: frame.precKappa }, this.srid);
        this.precOmega = std_opk.omega;
        this.precPhi = std_opk.phi;
        this.precKappa = std_opk.kappa;
        this.dataPath = frame.dataPath;
    }
    setParentGeoFrameCollection(parentGeoFrameCollection) {
        this.parentGeoFrameCollection = parentGeoFrameCollection;
    }
    get framenumber() {
        return this.getFramenumber();
    }
    getFramenumber() {
        if (this._framenumber) {
            return this._framenumber;
        }
        if (this.parentGeoFrameCollection) {
            return this.parentGeoFrameCollection.geoframearray.findIndex((e) => e.key == this.key);
        }
        return undefined;
    }
    getPosition() {
        return [this.easting, this.northing, this.height];
    }
    getOrientation() {
        return [this.omega, this.phi, this.kappa];
    }
    getHorizontalOrientation() {
        const omega = (this.omega * Math.PI) / 180;
        const phi = (this.phi * Math.PI) / 180;
        const x = -1 * Math.sin(phi);
        const y = Math.sin(omega) * Math.cos(phi);
        let azi = 0;
        if (x > 0 && y > 0) {
            azi = (Math.atan(x / y) * 180) / Math.PI;
        }
        else if (x > 0 && y < 0) {
            azi = ((Math.atan(x / y) + Math.PI) * 180) / Math.PI;
        }
        else if (x < 0 && y < 0) {
            azi = ((Math.atan(x / y) + Math.PI) * 180) / Math.PI;
        }
        else if (x < 0 && y > 0) {
            azi = ((Math.atan(x / y) + 2 * Math.PI) * 180) / Math.PI;
        }
        return azi;
    }
    getVerticalOrientation() {
        const omega = (this.omega * Math.PI) / 180;
        const phi = (this.phi * Math.PI) / 180;
        const x = -1 * Math.sin(phi);
        const y = Math.sin(omega) * Math.cos(phi);
        const z = Math.cos(omega) * Math.cos(phi);
        return (Math.atan(z / Math.sqrt(x * x + y * y)) * 180) / Math.PI;
    }
    /**
     * Downloads the bounding box on current frame by a specified LOD.
     */
    downloadCurrentFrame(x0, y0, x1, y1, lod, cbf) {
        if (x0 > 1)
            x0 = 1;
        if (x0 < 0)
            x0 = 0;
        if (x1 > 1)
            x1 = 1;
        if (x1 < 0)
            x1 = 0;
        if (y0 > 1)
            y0 = 1;
        if (y0 < 0)
            y0 = 0;
        if (y1 > 1)
            y1 = 1;
        if (y1 < 0)
            y1 = 0;
        let tmp;
        //Sort the coordinates
        if (x0 > x1) {
            tmp = x0;
            x0 = x1;
            x1 = tmp;
        }
        if (y0 > y1) {
            tmp = y0;
            y0 = y1;
            y1 = tmp;
        }
        this.cbf = cbf;
        const quadtreesize = Math.pow(2, lod) * this.framedatameta.tilesize;
        this._x0 = x0 * quadtreesize;
        this._y0 = y0 * quadtreesize;
        this._x1 = x1 * quadtreesize;
        this._y1 = y1 * quadtreesize;
        this._tilecounter = 0;
        this._tilelist = [];
        //calc the tilelist
        this._quadcodes = QuadtreeUtils.GetQuadKeysWithinNormalizedBoundingBox(x0, y0, x1, y1, lod);
        //request the tiles from cache
        for (let i = 0; i < this._quadcodes.length; i++) {
            const cQ = this._quadcodes[i];
            const baseUrl = this.dataPath;
            const iCbf = (imagePackage) => {
                this._tilelist.push({
                    quadCode: imagePackage.quadCode,
                    image: imagePackage.image,
                    loaded: imagePackage.loaded,
                });
                if (this._tilelist.length >= this._quadcodes.length) {
                    this._waitForAllTilesLoaded();
                }
            };
            // FIXME: Maybe dispatch differently, on this.type. Refactor so no type check. Typeguard or other.
            if (this.parentGeoFrameCollection instanceof StereoGeoFrameCollection) {
                if (this.parentGeoFrameCollection.rightimagequadtree.active) {
                    this.parentGeoFrameCollection.rightimagequadtree.requestImageTile(baseUrl, cQ, iCbf);
                }
                else {
                    this.parentGeoFrameCollection.imagequadtree.requestImageTile(baseUrl, cQ, iCbf);
                }
            }
            else {
                this.parentGeoFrameCollection.imagequadtree.requestImageTile(baseUrl, cQ, iCbf);
            }
        }
    }
    _waitForAllTilesLoaded() {
        // create an empty canvas
        const canv = document.createElement("canvas");
        const quadtreesize = Math.pow(2, this._tilelist[0].quadCode.length) * this._tilelist[0].image.width;
        // determine the offset for drawing
        let coords = QuadtreeUtils.QuadKeyToNormalizedCoord(this._quadcodes[0]);
        const offset_x = Math.max(coords[0] * quadtreesize, this._x0);
        const offset_y = Math.max(coords[1] * quadtreesize, this._y0);
        coords = QuadtreeUtils.QuadKeyToNormalizedCoord(this._quadcodes[this._quadcodes.length - 1]);
        canv.width = Math.round(Math.min(coords[2] * quadtreesize - offset_x, this._x1 - offset_x));
        canv.height = Math.round(Math.min(coords[3] * quadtreesize - offset_y, this._y1 - offset_y));
        const ctx2d = canv.getContext("2d");
        // blit the images on 2d canvas according to their quadcode
        for (let i = 0; i < this._tilelist.length; i++) {
            const tile = this._tilelist[i];
            if (!tile.loaded) {
                continue;
            }
            coords = QuadtreeUtils.QuadKeyToNormalizedCoord(tile.quadCode);
            coords[0] *= quadtreesize;
            coords[1] *= quadtreesize;
            coords[2] *= quadtreesize;
            coords[3] *= quadtreesize;
            ctx2d.drawImage(tile.image, -Math.round(offset_x) + coords[0], -Math.round(offset_y) + coords[1]);
        }
        const dataURL = canv.toDataURL("image/png");
        if (this.cbf !== null) {
            this.cbf.call(this, dataURL);
        }
        else {
            window.open(dataURL);
        }
    }
    // FIXME: This does not make sense. If there is only one sensor.
    getRightProjectionCenter() {
        const Phi = (this.phi * Math.PI) / 180;
        const Omega = (this.omega * Math.PI) / 180;
        const Kappa = (this.kappa * Math.PI) / 180;
        const matRot = new Matrix4();
        matRot.set(Math.cos(Phi) * Math.cos(Kappa), -Math.cos(Phi) * Math.sin(Kappa), Math.sin(Phi), 0, Math.cos(Omega) * Math.sin(Kappa) + Math.sin(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.cos(Omega) * Math.cos(Kappa) - Math.sin(Omega) * Math.sin(Phi) * Math.sin(Kappa), -Math.sin(Omega) * Math.cos(Phi), 0, Math.sin(Omega) * Math.sin(Kappa) - Math.cos(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.sin(Omega) * Math.cos(Kappa) + Math.cos(Omega) * Math.sin(Phi) * Math.sin(Kappa), Math.cos(Omega) * Math.cos(Phi), 0, 0, 0, 0, 1);
        const Projektionszentrum = new Vector3(this.easting, this.northing, this.height);
        // FIXME: Guard [1] and this will not work if not stereo.
        const pr = new Vector3(this.framedatameta.depthmapbasis, 0, 0);
        const p = new Vector3(pr.x, pr.y, pr.z).applyMatrix4(matRot);
        const Pos3d = new Vector3(p.x, p.y, p.z);
        Pos3d.add(Projektionszentrum);
        return Pos3d.toArray();
    }
    getRotationMatrix() {
        const o = (this.omega * Math.PI) / 180;
        const p = (this.phi * Math.PI) / 180;
        const k = (this.kappa * Math.PI) / 180;
        return [
            [Math.cos(p) * Math.cos(k), -Math.cos(p) * Math.sin(k), Math.sin(p)],
            [
                Math.cos(o) * Math.sin(k) + Math.sin(o) * Math.sin(p) * Math.cos(k),
                Math.cos(o) * Math.cos(k) - Math.sin(o) * Math.sin(p) * Math.sin(k),
                -Math.sin(o) * Math.cos(p),
            ],
            [
                Math.sin(o) * Math.sin(k) - Math.cos(o) * Math.sin(p) * Math.cos(k),
                Math.sin(o) * Math.cos(k) + Math.cos(o) * Math.sin(p) * Math.sin(k),
                Math.cos(o) * Math.cos(p),
            ],
        ];
    }
    getRotationMatrixAsMat4() {
        const Omega = (this.omega * Math.PI) / 180;
        const Phi = (this.phi * Math.PI) / 180;
        const Kappa = (this.kappa * Math.PI) / 180;
        const m = new Matrix4();
        m.set(Math.cos(Phi) * Math.cos(Kappa), -Math.cos(Phi) * Math.sin(Kappa), Math.sin(Phi), 0, Math.cos(Omega) * Math.sin(Kappa) + Math.sin(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.cos(Omega) * Math.cos(Kappa) - Math.sin(Omega) * Math.sin(Phi) * Math.sin(Kappa), -Math.sin(Omega) * Math.cos(Phi), 0, Math.sin(Omega) * Math.sin(Kappa) - Math.cos(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.sin(Omega) * Math.cos(Kappa) + Math.cos(Omega) * Math.sin(Phi) * Math.sin(Kappa), Math.cos(Omega) * Math.cos(Phi), 0, 0, 0, 0, 1);
        return m;
    }
    // FIXME: This needs to be reviewed in detail. Maybe refactor the crazy MeasureInfo.
    //        For now I am programming agains this interface.
    // FIXME: How are these here created left, right n sensor? What is a Geoframe really.
    getMeasureInfo() {
        const sensors_internal = [];
        for (const key in this.capturesystem.sensors) {
            const sensor = this.capturesystem.sensors[key];
            const sensor_internal = {
                name: "unknown",
                focallength: sensor.focallength,
                pos: sensor.pos,
                position: {
                    dx: sensor.bx,
                    dy: sensor.by2,
                    dz: sensor.bz,
                    domega: sensor.domega,
                    dphi: sensor.dphi,
                    dkappa: sensor.dkappa,
                },
                mosaic: sensor.mosaic,
            };
            sensors_internal.push(sensor_internal);
        }
        sensors_internal.sort((a, b) => {
            return a["pos"] - b["pos"];
        });
        const minfo = {
            description: this.capturesystem.description,
            height: this.capturesystem.height,
            idsensorarray: this.capturesystem.id,
            name: this.capturesystem.name,
            orientation: this.capturesystem.orientation,
            projection: MeasureInfo.projectionToNumber(this.projection),
            sensorgroup: this.capturesystem.sensorgroup,
            tilt: this.capturesystem.tilt,
            tooltip: this.capturesystem.tooltip,
            sensors: sensors_internal,
            imagemeta: {
                format: this.framedatameta.imageformat,
                height: this.framedatameta.imageheight,
                idimagemeta: this.framedatameta.id,
                maxzoom: this.framedatameta.imagemaxzoom,
                minzoom: 1, // this.framedatameta.imageminzoom,
                pixsize: this.framedatameta.imagepixsize,
                structure: this.framedatameta.imagestructure,
                tilesize: this.framedatameta.tilesize,
                width: this.framedatameta.imagewidth,
                focallength: this.framedatameta.imagefocallength,
            },
            depthmapmeta: {
                format: this.framedatameta.depthmapformat,
                height: this.framedatameta.depthmapheight,
                maxzoom: this.framedatameta.depthmapmaxzoom,
                minzoom: 1, // this.framedatameta.depthmapminzoom,
                pixsize: this.framedatameta.depthmappixsize,
                structure: this.framedatameta.depthmapstructure,
                tilesize: this.framedatameta.tilesize,
                width: this.framedatameta.depthmapwidth,
                numdisparities: this.framedatameta.depthmapnumdisparities,
                focallength: this.framedatameta.depthmapfocallength,
                basis: this.framedatameta.depthmapbasis,
            },
            precFocallength: this.framedatameta.precFocallength,
            precBasis: this.framedatameta.precBasis,
            precDisparity: this.framedatameta.precDisparity,
            streamtype: MeasureInfo.streamtypeToNumber(this.type),
        };
        return new MeasureInfo(minfo, null);
    }
    /**
     * Pans/zooms geoframe so that the {@link centerpointfeature- centerpointfeature} is in the center with {@link _zoomwindowsize- _zoomwindowsize} wide border.
     */
    zoomToCenterPoint(coords, mindist, maxdist, zoomwindowsize, withLookAtFeature = true, cubeorientation) {
        // FIXME: Refactor entire code. To be switch-less and less a mess!
        if (withLookAtFeature) {
            // FIXME: Refactor.
            if (!this.parentGeoFrameCollection.lookatfc) {
                this.parentGeoFrameCollection.lookatfc = this.scene
                    .getFeatureHandler()
                    .createFeatureCollection(this.scene.getToolHandler().defaultstyles["lookAt"], null, "mmStream_internal", false);
            }
            const lookatfeature = this.parentGeoFrameCollection.lookatfc.createFeature();
            lookatfeature.geometry = lookatfeature.createGeometry(GEOMETRYTYPE.POINT);
            lookatfeature.geometry.coordinates = [coords.slice(0)];
            lookatfeature.update();
        }
        const mh = this.scene.getMeasureHandler();
        const world = new Vector3(coords[0], coords[1], coords[2]);
        let mapinfos;
        if (this.type == "cubemap") {
            mapinfos = mh.worldToMapinfo(world, this, mindist, maxdist, cubeorientation);
        }
        else {
            mapinfos = mh.worldToMapinfo(world, this, mindist, maxdist);
        }
        const _zoomwindowsize = zoomwindowsize || 1.0;
        if (mapinfos.length <= 0) {
            return;
        }
        const centerpoint = new Vector3();
        let latlong;
        const g = _zoomwindowsize;
        let fov;
        const activeImage = this.scene.getCurrentGeoFrameCollection()
            .activeimage;
        let sensorid = 0;
        let model;
        let model_ul;
        let model_lr;
        let sensor_ul;
        let sensor_lr;
        let canvasratio;
        let boxsize;
        switch (this.type) {
            case "calotte":
                mapinfos.sort((a, b) => {
                    return a.distance - b.distance;
                });
                latlong = this.scene
                    .getMeasureHandler()
                    .mapToLatLong(mapinfos[0].coords, mapinfos[0].sensorid, this, centerpoint);
                fov = (Math.asin(g / Math.sqrt(Math.pow(mapinfos[0].distance, 2) + Math.pow(g, 2))) * 180) / Math.PI;
                this.scene.infrascene.panoCamera.setInitLatLon(latlong.x, latlong.y, fov);
                break;
            case "cubemap":
                mapinfos.sort((a, b) => {
                    return a.distance - b.distance;
                });
                latlong = this.scene
                    .getMeasureHandler()
                    .mapToLatLong(mapinfos[0].coords, mapinfos[0].sensorid, this, centerpoint, mapinfos[0].cubefaceindex);
                fov = (Math.asin(g / Math.sqrt(Math.pow(mapinfos[0].distance, 2) + Math.pow(g, 2))) * 180) / Math.PI;
                this.scene.infrascene.multiperspectiveCamera.setInitLatLon(latlong.x, latlong.y, fov);
                break;
            default:
                if (activeImage == "right")
                    sensorid = 1;
                model = mh.worldToModel(world, sensorid, this);
                model_ul = new Vector3(model.x - _zoomwindowsize / 2, model.y + _zoomwindowsize / 2, model.z);
                model_lr = new Vector3(model.x + _zoomwindowsize / 2, model.y - _zoomwindowsize / 2, model.z);
                sensor_ul = mh.imageToSensor(mh.modelToImage(model_ul, sensorid, this), this);
                sensor_lr = mh.imageToSensor(mh.modelToImage(model_lr, sensorid, this), this);
                canvasratio =
                    this.scene.infrascene.engine.getContextWidth() / this.scene.infrascene.engine.getContextHeight();
                boxsize = sensor_lr.x - sensor_ul.x;
                if (boxsize > 100) {
                    if (canvasratio >= 1) {
                        sensor_ul.x -= (boxsize * canvasratio - boxsize) / 2;
                        sensor_lr.x += (boxsize * canvasratio - boxsize) / 2;
                    }
                    else {
                        sensor_ul.y -= (boxsize / canvasratio - boxsize) / 2;
                        sensor_lr.y += (boxsize / canvasratio - boxsize) / 2;
                    }
                    this.scene
                        .getCurrentGeoFrameCollection()
                        .fitToExtent(sensor_ul.x, sensor_ul.y, sensor_lr.x, sensor_lr.y);
                }
                else {
                    this.scene.getCurrentGeoFrameCollection().fitToCanvas();
                }
        }
    }
    pointTolatlonfov(pointinginfo) {
        switch (this.type) {
            case "calotte":
                pointinginfo = pointinginfo;
                this.scene.infrascene.panoCamera.setInitLatLon(pointinginfo.lat, pointinginfo.lon, pointinginfo.fov);
                break;
            case "cubemap":
                pointinginfo = pointinginfo;
                this.scene.infrascene.multiperspectiveCamera.setInitLatLon(pointinginfo.lat, pointinginfo.lon, pointinginfo.fov);
                break;
        }
    }
    pointTopanzoom(pointinginfo) {
        pointinginfo = pointinginfo;
        this.scene.infrascene.quadtreeCamera.setZoomPan(pointinginfo.zoom || 1, pointinginfo.panX || 0, pointinginfo.panY || 0);
    }
    copy() {
        const coords = proj4(`EPSG:${this.srid}`, "EPSG:4326", {
            x: this.easting,
            y: this.northing,
            z: this.height,
        });
        const opk = Spatial.rotToWGS84({ easting: coords.x, northing: coords.y, height: this.height }, { omega: this.omega, phi: this.phi, kappa: this.kappa }, this.srid);
        const std_opk = Spatial.rotToWGS84({ easting: this.easting, northing: this.northing, height: this.height }, { omega: this.precOmega, phi: this.precPhi, kappa: this.precKappa }, this.srid);
        return new GeoFrame(this.scene, 
        // HACK
        new Frame(this.id, this.key, this.collection, this.stream, this.type, this.segment, this.framenumber, this.projection, this.timestamp, this.images, this.playmode, coords.x, coords.y, this.height, opk.omega, opk.phi, opk.kappa, this.precEasting, this.precNorthing, this.precHeight, std_opk.omega, std_opk.phi, std_opk.kappa, this.capturesystemID, this.framedatametaID, this.campaignID, this.capturesystem, this.framedatameta, this.campaign, this.srid, this.sridHeight, this.dataPath));
    }
}
//# sourceMappingURL=geoframe.js.map